import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { LogOut, Inbox } from "lucide-react";

export default function AdminDashboard() {
  const [leads, setLeads] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        await api.get("/auth/me");
        const { data } = await api.get("/leads");
        setLeads(data);
      } catch {
        nav("/admin");
      }
    })();
  }, [nav]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    nav("/admin");
  };

  return (
    <div className="min-h-screen bg-cream grain">
      <header className="border-b border-clubborder bg-cream/90 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <Logo />
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-ink/60 hover:text-forest transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <h1 className="font-sans font-extrabold tracking-tight text-4xl sm:text-5xl">Leads</h1>
            <p className="text-ink/50 mt-2">Every enquiry that's come through the booking form.</p>
          </div>
          <div className="rounded-full bg-forest text-cream px-5 py-2.5 text-sm" data-testid="lead-count">
            {leads ? `${leads.length} total` : "…"}
          </div>
        </div>

        {leads === null && <p className="text-ink/40 py-20 text-center">Loading…</p>}

        {leads && leads.length === 0 && (
          <div className="text-center py-24 border border-dashed border-clubborder rounded-2xl">
            <Inbox className="mx-auto text-forest/40 mb-4" size={40} />
            <p className="font-serif text-3xl">No leads yet.</p>
            <p className="text-ink/50 mt-2">They'll show up here the moment someone checks their date.</p>
          </div>
        )}

        {leads && leads.length > 0 && (
          <div className="overflow-x-auto border border-clubborder rounded-2xl bg-white" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink/40 uppercase text-xs tracking-wider border-b border-clubborder">
                  <th className="p-4">Name</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">Event</th>
                  <th className="p-4">Date</th>
                  <th className="p-4">Location</th>
                  <th className="p-4">Travel</th>
                  <th className="p-4">Hours</th>
                  <th className="p-4">Source</th>
                  <th className="p-4">Received</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-clubborder/60 last:border-0 hover:bg-clubmuted/40" data-testid={`lead-row-${l.id}`}>
                    <td className="p-4 font-medium">
                      {l.name}
                      {l.message ? <p className="text-ink/40 text-xs mt-1 max-w-[180px] truncate">{l.message}</p> : null}
                    </td>
                    <td className="p-4 text-ink/70">
                      <a href={`mailto:${l.email}`} className="text-forest hover:underline block">{l.email}</a>
                      <span className="text-ink/50">{l.phone}</span>
                    </td>
                    <td className="p-4">{l.event_type}</td>
                    <td className="p-4">{l.event_date}</td>
                    <td className="p-4">{l.venue_city}<br /><span className="text-ink/40">{l.postal_code}</span></td>
                    <td className="p-4 text-ink/60 max-w-[160px]">{l.travel_estimate || "—"}</td>
                    <td className="p-4">{l.estimated_hours}</td>
                    <td className="p-4 text-ink/60">{l.heard_from || "—"}</td>
                    <td className="p-4 text-ink/40">{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
